import type { Middleware } from 'openapi-fetch'
import { getCsrfToken, invalidateSession } from '../auth/session-store'

/** Methods that never need the CSRF header (they can't mutate state). */
const CSRF_SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

/**
 * Auth-surface endpoints (apps/api/src/modules/bff/routes.ts) manage their
 * own auth-failure UX and must NOT trip the global "session expired →
 * invalidate" behavior on a 401:
 *  - `/auth/session` returning 401 is the ordinary "not logged in yet"
 *    signal for an anonymous visitor, not a session expiring mid-use — the
 *    caller (auth-context.ts) already treats a 401 here as "unauthenticated"
 *    directly. Invalidating from the middleware too would be redundant at
 *    best and, on every anonymous page load, is the literal opposite of a
 *    problem — but is excluded on principle so the ONE place that decides
 *    "was this session valid before this call" is the caller, not a global
 *    side effect on the exact endpoint used to find that out.
 *  - `/auth/login`, `/auth/register`, `/auth/verify-email` are
 *    pre-authentication flows; a 401/403 from these is a credential failure
 *    the calling form handles inline, not a signal that an existing session
 *    expired.
 *  - `/auth/logout` is the user's own explicit sign-out; its caller clears
 *    local state on success regardless.
 *  - `/onboarding/account` (USP-43): a 401 here means the `onboarding_required`
 *    pre-onboarding session's absolute TTL expired mid-wizard
 *    (`requireOnboardingUser`, apps/api/src/modules/onboarding/routes.ts). The
 *    same rationale as `/auth/login` et al. applies — this is a case the
 *    calling form (`<CreateCompanyPage>`) handles inline, not a signal that a
 *    fully-authenticated session expired. Before this exclusion, the global
 *    `invalidateSession()` side effect collapsed `onboarding_required` straight
 *    to `unauthenticated` and raced out the page's own "your session
 *    expired" error before it could render — see CreateCompanyPage.tsx's
 *    `handleCreate`.
 *  - `/onboarding/join-requests` (F0.7, same USP-43 class): its guard
 *    (`requireLiveSession`, apps/api/src/modules/onboarding/routes.ts) is
 *    session-liveness-only, so the identical absolute-TTL-expired-mid-flow
 *    case applies to `<RequestToJoinPage>` as it does to
 *    `<CreateCompanyPage>`'s `/onboarding/account` call — a 401 here must be
 *    handled inline by the calling page, not raced out by a global
 *    unauthenticated flip before that page's own "your session expired"
 *    branch (which also runs off this same response) gets a chance to render.
 */
const AUTH_SURFACE_PATHS: ReadonlySet<string> = new Set([
  '/auth/session',
  '/auth/login',
  '/auth/register',
  '/auth/verify-email',
  '/auth/logout',
  '/onboarding/account',
  '/onboarding/join-requests',
])

/**
 * The ONE authed-fetch wrapper every API call goes through (apps/web/src/lib/api/client.ts
 * is the only place that installs it) — per F0.2 scope, no second hand-rolled
 * client. Two responsibilities:
 *
 * 1. Attach the CSRF double-submit token (C22) as the `csrf-token` header on
 *    every state-changing request, when we have one. The token itself came
 *    from `GET /auth/session`'s JSON body — never a cookie the browser reads
 *    (the session cookie stays httpOnly; JS never touches it).
 * 2. Detect a session that expired or was revoked mid-use (a 401 from an
 *    ordinary, non-auth-surface endpoint while the SPA still thought it was
 *    authenticated) and flip the shared session store to `unauthenticated` —
 *    the one signal `useAuth()` consumers and the 401 redirect (see
 *    auth-context.tsx) key off of. This never loops: {@link invalidateSession}
 *    is a no-op once already unauthenticated, and re-authenticating always
 *    goes through a full-page navigation to the BFF, not another fetch.
 */
export const authMiddleware: Middleware = {
  onRequest({ request }) {
    if (CSRF_SAFE_METHODS.has(request.method)) {
      return request
    }
    const csrfToken = getCsrfToken()
    if (!csrfToken) {
      return request
    }
    request.headers.set('csrf-token', csrfToken)
    return request
  },
  onResponse({ schemaPath, response }) {
    if (response.status === 401 && !AUTH_SURFACE_PATHS.has(schemaPath)) {
      invalidateSession()
    }
    return response
  },
}
